import { createContext, FC } from 'react';
import ParnerUserDrawer from '@/components/drawers/partner-user';
import OrgDrawer from '@/components/drawers/org';
import PartnerDrawer from '@/components/drawers/partner';
import { getQueryStringParams, patchQueryParams } from '@/lib/url-helpers';
import { JsonObject } from '@/lib/helpers';
import { TabVariant } from '@/hooks/use-tabs.hook';
import { DrawerProps } from '@/components/drawers/helpers';

export type  DrawerType = 'partner' | 'partner-user' | 'org';

export const drawerMap: Map<DrawerType, FC> = new Map();
drawerMap.set('partner-user', ParnerUserDrawer);
drawerMap.set('org', OrgDrawer);
drawerMap.set('partner', PartnerDrawer);

export type DrawerConfig = {
  drawerType: DrawerType;
  props: DrawerProps;
  closing?: 'all' | 'self';
  mounted: boolean;
  onClose: () => void;
};

export interface IDrawerState {
  openDrawer: (drawerType: DrawerType, addlProps?: Record<string, unknown>) => void;
  closeDrawer: (force?: boolean) => void;
  goBackDrawer: (force?: boolean) => void;
  stack: DrawerConfig[];
}

export const DrawerContext = createContext<IDrawerState>({
  openDrawer: () => null,
  closeDrawer: () => null,
  goBackDrawer: () => null,
  stack: [],
});

type DrawerUrlParams = { drawer: DrawerType } & Record<string, string | boolean>;

export const setUrlParams = (
  drawerType: DrawerType,
  drawerProps: JsonObject = {},
  clear?: boolean
) => {
  const urlParams: Record<string, string | null> = {
    drawer: drawerType,
  };

  const allowedParams = ['drawer', 'tab', 'id', 'partnerId'];

  allowedParams.forEach((key) => {
    let propVal = drawerProps[key];

    if (key === 'tab' && typeof propVal === 'string') {
      urlParams[TabVariant.drawer] = propVal;
      return;
    }

    // convert boolean to stringified version
    if (typeof propVal === 'boolean') {
      propVal = propVal ? 'true' : 'false';
    }

    if (typeof propVal === 'number') {
      propVal = String(propVal);
    }

    if (typeof propVal === 'string') {
      urlParams[`dp_${key}`] = propVal;
      return;
    }

    if (clear) {
      urlParams[`dp_${key}`] = null;
    }
  });

  patchQueryParams(urlParams, true);
};

export const clearUrlParams = () => {
  const urlParams = getQueryStringParams();

  const clearParams: Record<string, null> = {
    drawer: null,
  };

  Object.keys(urlParams)
    .filter((key) => key.startsWith('dp_'))
    .forEach((key) => {
      clearParams[key] = null;
    });

  patchQueryParams(clearParams, true);
};

export const getUrlParams = (drawerType?: DrawerType): DrawerUrlParams | null => {
  const { drawer: drawerParam, ...rest } = getQueryStringParams();
  if (drawerType && drawerParam !== drawerType) {
    return null;
  }

  if (!drawerMap.has(drawerParam as DrawerType)) {
    return null;
  }

  const drawer = drawerParam as DrawerType;
  const urlParams: DrawerUrlParams = { drawer };

  Object.keys(rest)
    .filter((key) => key.startsWith('dp_'))
    .forEach((key) => {
      let urlVal: string | boolean = rest[key];

      // convert stringified boolean back to type boolean
      if (['true', 'false'].includes(urlVal)) {
        urlVal = urlVal === 'true';
      }

      urlParams[key.replace(/^dp_/, '')] = urlVal;
    });

  return urlParams;
};
